import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Save, Trash2, Sprout } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ICONS, ICON_NAMES, DEFAULT_SERVICE_IMAGE } from "@/lib/services";

const EMPTY = { name: "", price: "", blurb: "", details: "", image: "", icon: "Sprout", active: true };

const inputCls =
  "w-full rounded-xl border border-emerald-950/15 bg-white px-3.5 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-limed/50";

function ServiceEditor({ service, onChange, onSave, onDelete, saving }) {
  const Icon = ICONS[service.icon] || Sprout;
  return (
    <div data-testid={`admin-service-card-${service.id}`} className="bg-white rounded-2xl border border-emerald-950/10 p-5">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-xl bg-sage flex items-center justify-center text-pine">
          <Icon size={18} />
        </div>
        <input
          data-testid={`service-name-input-${service.id}`}
          value={service.name}
          onChange={(e) => onChange(service.id, "name", e.target.value)}
          placeholder="Service name"
          className={`${inputCls} flex-1 min-w-[160px] font-semibold`}
        />
        <input
          data-testid={`service-price-input-${service.id}`}
          value={service.price}
          onChange={(e) => onChange(service.id, "price", e.target.value)}
          placeholder="e.g. Starting at $40"
          className={`${inputCls} w-44`}
        />
        <select
          data-testid={`service-icon-select-${service.id}`}
          value={service.icon}
          onChange={(e) => onChange(service.id, "icon", e.target.value)}
          className={`${inputCls} w-32 bg-white`}
        >
          {ICON_NAMES.map((n) => (
            <option key={n} value={n}>{n}</option>
          ))}
        </select>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <input
          data-testid={`service-blurb-input-${service.id}`}
          value={service.blurb || ""}
          onChange={(e) => onChange(service.id, "blurb", e.target.value)}
          placeholder="Short description"
          className={inputCls}
        />
        <input
          data-testid={`service-image-input-${service.id}`}
          value={service.image || ""}
          onChange={(e) => onChange(service.id, "image", e.target.value)}
          placeholder="Image URL"
          className={inputCls}
        />
        <textarea
          data-testid={`service-details-input-${service.id}`}
          value={service.details || ""}
          onChange={(e) => onChange(service.id, "details", e.target.value)}
          placeholder="Details shown when expanded"
          rows={2}
          className={`${inputCls} sm:col-span-2 resize-none`}
        />
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <label className="inline-flex items-center gap-2 text-sm text-emerald-900/70 cursor-pointer">
          <input
            data-testid={`service-active-toggle-${service.id}`}
            type="checkbox"
            checked={!!service.active}
            onChange={(e) => onChange(service.id, "active", e.target.checked)}
            className="w-4 h-4 accent-limed"
          />
          Visible on site
        </label>
        <div className="flex gap-2">
          <button
            data-testid={`service-save-btn-${service.id}`}
            onClick={() => onSave(service)}
            disabled={saving === service.id}
            className="inline-flex items-center gap-1.5 text-xs font-semibold bg-pine text-bone px-4 py-2 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-50"
          >
            {saving === service.id ? <Loader2 size={13} className="animate-spin" /> : <Save size={13} />}
            Save
          </button>
          <button
            data-testid={`service-delete-btn-${service.id}`}
            onClick={() => onDelete(service)}
            className="inline-flex items-center gap-1.5 text-xs font-semibold border border-emerald-950/15 text-emerald-900/60 px-4 py-2 rounded-full hover:border-red-300 hover:text-red-500 transition-colors"
          >
            <Trash2 size={13} /> Delete
          </button>
        </div>
      </div>
    </div>
  );
}

export default function ServicesTab() {
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(null);
  const [adding, setAdding] = useState(false);
  const [newService, setNewService] = useState(EMPTY);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/services");
      setServices(data);
    } catch (err) {
      toast.error(formatApiError(err, "Couldn't load services."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const onChange = (id, key, value) =>
    setServices((list) => list.map((s) => (s.id === id ? { ...s, [key]: value } : s)));

  const onSave = async (service) => {
    setSaving(service.id);
    try {
      const { id, order, created_at, ...payload } = service;
      await api.patch(`/admin/services/${id}`, payload);
      toast.success(`"${service.name}" saved.`);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(null);
    }
  };

  const onDelete = async (service) => {
    if (!window.confirm(`Delete "${service.name}"? It disappears from the site immediately.`)) return;
    try {
      await api.delete(`/admin/services/${service.id}`);
      toast.success("Service deleted.");
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const onAdd = async () => {
    if (!newService.name.trim() || !newService.price.trim()) {
      return toast.error("Give the service a name and a price.");
    }
    setSaving("new");
    try {
      await api.post("/admin/services", {
        ...newService,
        image: newService.image || DEFAULT_SERVICE_IMAGE,
      });
      toast.success(`"${newService.name}" added to the site.`);
      setNewService(EMPTY);
      setAdding(false);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-20 text-pine">
        <Loader2 size={28} className="animate-spin" />
      </div>
    );
  }

  return (
    <div data-testid="admin-services-panel" className="space-y-4">
      <p className="text-sm text-emerald-900/60">
        Edit names, prices, descriptions and photos — changes go live on the site as soon as you
        save. Uncheck "Visible on site" to hide a service without deleting it.
      </p>
      {services.map((s) => (
        <ServiceEditor
          key={s.id}
          service={s}
          onChange={onChange}
          onSave={onSave}
          onDelete={onDelete}
          saving={saving}
        />
      ))}

      {adding ? (
        <div className="bg-limed-light/40 rounded-2xl border-2 border-dashed border-limed/50 p-5">
          <p className="font-display font-bold text-emerald-950 mb-4">New service</p>
          <div className="flex flex-wrap items-center gap-3 mb-4">
            <input
              data-testid="service-new-name"
              value={newService.name}
              onChange={(e) => setNewService({ ...newService, name: e.target.value })}
              placeholder="Service name"
              className={`${inputCls} flex-1 min-w-[160px]`}
            />
            <input
              data-testid="service-new-price"
              value={newService.price}
              onChange={(e) => setNewService({ ...newService, price: e.target.value })}
              placeholder="e.g. Starting at $50"
              className={`${inputCls} w-44`}
            />
            <select
              data-testid="service-new-icon"
              value={newService.icon}
              onChange={(e) => setNewService({ ...newService, icon: e.target.value })}
              className={`${inputCls} w-32 bg-white`}
            >
              {ICON_NAMES.map((n) => (
                <option key={n} value={n}>{n}</option>
              ))}
            </select>
          </div>
          <div className="grid sm:grid-cols-2 gap-3">
            <input
              data-testid="service-new-blurb"
              value={newService.blurb}
              onChange={(e) => setNewService({ ...newService, blurb: e.target.value })}
              placeholder="Short description"
              className={inputCls}
            />
            <input
              data-testid="service-new-image"
              value={newService.image}
              onChange={(e) => setNewService({ ...newService, image: e.target.value })}
              placeholder="Image URL (optional)"
              className={inputCls}
            />
            <textarea
              data-testid="service-new-details"
              value={newService.details}
              onChange={(e) => setNewService({ ...newService, details: e.target.value })}
              placeholder="Details shown when expanded"
              rows={2}
              className={`${inputCls} sm:col-span-2 resize-none`}
            />
          </div>
          <div className="mt-4 flex gap-2">
            <button
              data-testid="service-add-confirm-btn"
              onClick={onAdd}
              disabled={saving === "new"}
              className="inline-flex items-center gap-1.5 text-xs font-semibold bg-pine text-bone px-5 py-2.5 rounded-full hover:bg-limed hover:text-pine-dark transition-colors disabled:opacity-50"
            >
              {saving === "new" ? <Loader2 size={13} className="animate-spin" /> : <Plus size={13} />}
              Add Service
            </button>
            <button
              data-testid="service-add-cancel-btn"
              onClick={() => setAdding(false)}
              className="text-xs font-semibold text-emerald-900/60 px-4 py-2.5"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <button
          data-testid="service-add-btn"
          onClick={() => setAdding(true)}
          className="w-full inline-flex items-center justify-center gap-2 border-2 border-dashed border-emerald-950/15 text-emerald-900/60 font-semibold py-4 rounded-2xl hover:border-limed hover:text-pine transition-colors"
        >
          <Plus size={16} /> Add a new service
        </button>
      )}
    </div>
  );
}
